import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import {
    Users, UserPlus, Mail, Phone, Shield,
    Trash2, Edit2, Loader2, Search, X, Check,
    TrendingUp, Briefcase, Wallet, Target
} from 'lucide-react';
import { formatError } from '../../utils/errorUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TeamPage() {
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [editingAgent, setEditingAgent] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        role: 'agent',
        total_sales: 0,
        total_clients: 0,
        sold_projects: '',
        commission_rate_sqft: 0,
        commission_rate_total: 0,
        total_earnings: 0
    });

    const getAuthHeader = () => {
        const token = localStorage.getItem('token');
        return { Authorization: `Bearer ${token}` };
    };

    const fetchTeam = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/team`, {
                headers: getAuthHeader()
            });
            setAgents(response.data);
        } catch (error) {
            toast.error('Failed to load team members');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTeam();
    }, []);

    const handleAddMember = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const payload = {
                ...formData,
                sold_projects: formData.sold_projects.split(',').map(s => s.trim()).filter(Boolean)
            };
            if (editingAgent) {
                await axios.patch(`${API_URL}/api/team/${editingAgent.id}`, payload, {
                    headers: getAuthHeader()
                });
                toast.success('Agent updated successfully');
            } else {
                await axios.post(`${API_URL}/api/team/add`, payload, {
                    headers: getAuthHeader()
                });
                toast.success('Team member added successfully');
            }
            setShowAddModal(false);
            setEditingAgent(null);
            resetForm();
            fetchTeam();
        } catch (error) {
            const message = formatError(error.response?.data?.detail) || 'Operation failed';
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '', email: '', phone: '', role: 'agent',
            total_sales: 0, total_clients: 0, sold_projects: '',
            commission_rate_sqft: 0, commission_rate_total: 0, total_earnings: 0
        });
    };

    const handleEdit = (agent) => {
        setEditingAgent(agent);
        setFormData({
            name: agent.name,
            email: agent.email,
            phone: agent.phone || '',
            role: agent.role,
            total_sales: agent.total_sales || 0,
            total_clients: agent.total_clients || 0,
            sold_projects: (agent.sold_projects || []).join(', '),
            commission_rate_sqft: agent.commission_rate_sqft || 0,
            commission_rate_total: agent.commission_rate_total || 0,
            total_earnings: agent.total_earnings || 0
        });
        setShowAddModal(true);
    };

    const handleDeleteMember = async (id) => {
        if (!window.confirm('Are you sure you want to remove this team member?')) return;
        try {
            await axios.delete(`${API_URL}/api/team/${id}`, {
                headers: getAuthHeader()
            });
            toast.success('Member removed');
            fetchTeam();
        } catch (error) {
            toast.error('Failed to remove member');
        }
    };

    const filteredAgents = agents.filter(agent =>
        agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        agent.email.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="p-6 lg:p-8 max-w-6xl mx-auto space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-heading font-bold text-navy-900">Team Management</h1>
                    <p className="text-navy-600">Manage your agency's agents and staff members</p>
                </div>
                <Button onClick={() => setShowAddModal(true)} className="bg-sky-500 hover:bg-sky-600">
                    <UserPlus className="w-4 h-4 mr-2" /> Add Team Member
                </Button>
            </div>

            <Card>
                <CardHeader className="pb-3 border-b border-navy-100 flex flex-row items-center justify-between">
                    <CardTitle className="text-lg font-semibold flex items-center gap-2">
                        <Users className="w-5 h-5 text-sky-500" />
                        Active Team Members ({agents.length})
                    </CardTitle>
                    <div className="relative w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
                        <Input
                            placeholder="Search by name or email..."
                            className="pl-9 h-9"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="py-20 flex flex-col items-center justify-center text-navy-400">
                            <Loader2 className="w-8 h-8 animate-spin mb-2" />
                            <p>Loading team...</p>
                        </div>
                    ) : filteredAgents.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-navy-50/50 text-navy-500 text-xs uppercase tracking-wider">
                                    <tr>
                                        <th className="px-6 py-4 font-semibold">Name & Role</th>
                                        <th className="px-6 py-4 font-semibold">Contact Details</th>
                                        <th className="px-6 py-4 font-semibold whitespace-nowrap">Performance Stats</th>
                                        <th className="px-6 py-4 font-semibold">Status</th>
                                        <th className="px-6 py-4 font-semibold text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-navy-50">
                                    {filteredAgents.map((agent) => (
                                        <tr key={agent.id} className="hover:bg-navy-50/30 transition-colors group">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-9 h-9 rounded-full bg-sky-100 text-sky-600 flex items-center justify-center font-bold text-sm">
                                                        {agent.name.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <p className="font-semibold text-navy-900">{agent.name}</p>
                                                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-tighter ${agent.role === 'manager' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                                                            }`}>
                                                            {agent.role}
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 space-y-1">
                                                <div className="flex items-center gap-2 text-sm text-navy-600">
                                                    <Mail className="w-3 h-3 text-navy-400" /> {agent.email}
                                                </div>
                                                {agent.phone && (
                                                    <div className="flex items-center gap-2 text-sm text-navy-600">
                                                        <Phone className="w-3 h-3 text-navy-400" /> {agent.phone}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col gap-1.5">
                                                    <div className="flex items-center gap-2 text-xs font-medium text-navy-600">
                                                        <TrendingUp className="w-3 h-3 text-emerald-500" />
                                                        <span>{agent.total_sales || 0} Sales</span>
                                                        <span className="text-navy-300">|</span>
                                                        <span>{agent.total_clients || 0} Clients</span>
                                                    </div>
                                                    <div className="flex items-center gap-2 text-[11px] text-navy-500">
                                                        <Wallet className="w-3 h-3 text-amber-500" />
                                                        <span>Earned: ₹{agent.total_earnings?.toLocaleString() || 0}</span>
                                                    </div>
                                                    {(agent.sold_projects || []).length > 0 && (
                                                        <div className="flex flex-wrap gap-1 mt-1">
                                                            {agent.sold_projects.slice(0, 2).map((p, i) => (
                                                                <span key={i} className="text-[9px] bg-navy-100 text-navy-600 px-1.5 py-0.5 rounded uppercase font-bold truncate max-w-[80px]">
                                                                    {p}
                                                                </span>
                                                            ))}
                                                            {agent.sold_projects.length > 2 && <span className="text-[9px] text-navy-400">+{agent.sold_projects.length - 2} more</span>}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="flex items-center gap-1.5 text-xs text-green-600 font-medium bg-green-50 px-2 py-1 rounded-full w-fit">
                                                    <Check className="w-3 h-3" /> Active
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex justify-end gap-1">
                                                    <button
                                                        onClick={() => handleEdit(agent)}
                                                        className="p-2 text-navy-400 hover:text-sky-600 transition-colors"
                                                        title="Edit Performance & Details"
                                                    >
                                                        <Edit2 className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteMember(agent.id)}
                                                        className="p-2 text-navy-400 hover:text-red-600 transition-colors"
                                                        title="Remove from team"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="py-20 text-center">
                            <Users className="w-12 h-12 text-navy-100 mx-auto mb-3" />
                            <p className="text-navy-500">No team members found.</p>
                            <Button
                                variant="outline"
                                size="sm"
                                className="mt-4"
                                onClick={() => setShowAddModal(true)}
                            >
                                Add your first agent
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Add Member Modal */}
            {showAddModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className="p-6 border-b border-navy-100 flex items-center justify-between bg-navy-50/50">
                            <h2 className="text-lg font-heading font-bold text-navy-900">
                                {editingAgent ? `Edit ${editingAgent.name}` : 'Add Team Member'}
                            </h2>
                            <button onClick={() => { setShowAddModal(false); setEditingAgent(null); resetForm(); }} className="text-navy-400 hover:text-navy-900">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <form onSubmit={handleAddMember} className="p-6 space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="name">Full Name</Label>
                                <Input
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="e.g., Rajesh Kumar"
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="email">Work Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    placeholder="e.g., rajesh@agency.com"
                                    required
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="role">Role</Label>
                                    <select
                                        id="role"
                                        className="flex h-10 w-full rounded-md border border-navy-200 bg-white px-3 py-2 text-sm shadow-sm ring-offset-white focus:outline-none focus:ring-2 focus:ring-sky-500"
                                        value={formData.role}
                                        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                    >
                                        <option value="agent">Agent</option>
                                        <option value="manager">Manager</option>
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="phone">Phone Number</Label>
                                    <Input
                                        id="phone"
                                        value={formData.phone}
                                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                        placeholder="+91..."
                                    />
                                </div>
                            </div>

                            <Separator />
                            <h3 className="text-sm font-bold text-navy-900 border-l-4 border-sky-500 pl-2 uppercase tracking-wider">Performance Metrics</h3>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-xs">Total Sales</Label>
                                    <Input
                                        type="number"
                                        value={formData.total_sales}
                                        onChange={(e) => setFormData({ ...formData, total_sales: parseInt(e.target.value) || 0 })}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-xs">Total Clients</Label>
                                    <Input
                                        type="number"
                                        value={formData.total_clients}
                                        onChange={(e) => setFormData({ ...formData, total_clients: parseInt(e.target.value) || 0 })}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-xs text-emerald-700">Earnings (₹)</Label>
                                    <Input
                                        type="number"
                                        value={formData.total_earnings}
                                        onChange={(e) => setFormData({ ...formData, total_earnings: parseFloat(e.target.value) || 0 })}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-xs">Comm. Rate (%)</Label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={formData.commission_rate_total}
                                        onChange={(e) => setFormData({ ...formData, commission_rate_total: parseFloat(e.target.value) || 0 })}
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label className="text-xs">Projects (Comma separated)</Label>
                                <Input
                                    value={formData.sold_projects}
                                    onChange={(e) => setFormData({ ...formData, sold_projects: e.target.value })}
                                    placeholder="Palm Jumeirah, Downtown..."
                                />
                            </div>
                            <div className="pt-4 flex gap-3">
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="flex-1"
                                    onClick={() => setShowAddModal(false)}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    className="flex-1 bg-sky-500 hover:bg-sky-600"
                                    disabled={submitting}
                                >
                                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingAgent ? 'Update Agent' : 'Add Member')}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
